import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useCountUp } from 'react-countup';
import { toShortNumFormat } from '@common/lib/toShortNumFormat';

import { safeCalculateMindedResources } from '@/common/lib/safeCalculateMindedResources';
import { selectBoostForQuarry } from '@/redux/selectors/quarrySelectors';
import { useAppSelector } from '@/redux/store';

interface ResourceStockProps {
  // kind: ResourceTypeText;
  plantId?: string;
  stock: number;
  limit: number;
  productionRate: number;
  updatedAt?: string;
  withoutFormat?: boolean;
}

const ResourceStock: FC<ResourceStockProps> = ({
  plantId,
  stock,
  limit,
  productionRate,
  updatedAt,
  withoutFormat = false,
}) => {
  const countUpRef = useRef<HTMLDivElement | null>(null);
  const [countStock, setCountStock] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const formattingFn = useCallback(
    (value: number): string => {
      return withoutFormat ? String(value) : String(toShortNumFormat(value));
    },
    [withoutFormat],
  );
  const boosts = useAppSelector((state) =>
    selectBoostForQuarry(state, plantId),
  );
  const updateStockCounter = useCallback(
    ({
      stock,
      productionRate,
      limit,
    }: {
      stock: number;
      limit: number;
      productionRate: number;
    }): void => {
      setCountStock((state) => {
        const updatedAtDate = new Date();
        const timePoint = new Date(updatedAtDate.getTime() + 60 * 1000);

        const plant = {
          updatedAt: updatedAtDate,
          productionRate,
          boosts: boosts || [],
          timePoint,
        };
        const producted = safeCalculateMindedResources(plant);
        const end = state ? state.end + producted : stock + producted;

        return {
          start: state ? state.end : stock,
          end: end > limit ? limit : end,
        };
      });
    },
    [boosts, updatedAt],
  );
  useCountUp({
    ref: countUpRef,
    useEasing: false,
    end: countStock?.end || 0,
    start: countStock?.start || 0,
    separator: '.',
    duration: 60,
    onEnd: () => updateStockCounter({ stock, limit, productionRate }),
    formattingFn,
  });

  useEffect(() => {
    if (stock || stock === 0) {
      setCountStock(null);
      updateStockCounter({ stock, limit, productionRate });
    }
  }, [stock, updatedAt, boosts]);

  return <div ref={countUpRef}></div>;
};

export { ResourceStock };
